<template>
  <FullScreenPanel
    :model-value="props.isOpen"
    :title="$t('commerce.filterModal.filters')"
    mobile-only
    @close="closeModal"
  >
    <div class="space-y-6">
      <!-- filters -->
      <div>
        <h4 class="dark:text-content-inverse font-medium mb-3 text-content-level-900 text-sm">
          {{ $t('commerce.filterModal.mealzFilters') }}
        </h4>

        <div class="flex flex-wrap gap-2">
          <button
            v-for="filter in mealzFilters"
            :key="filter.id"
            type="button"
            class="font-medium px-3 py-1.5 rounded-full text-xs transition-colors"
            :class="[
              filter.isActive
                ? 'bg-primary text-content-inverse'
                : 'bg-surface-level-100 dark:bg-surface-level-700 text-content-level-700 dark:text-content-level-300 hover:bg-surface-level-200 dark:hover:bg-surface-level-600'
            ]"
            :data-filter-id="filter.id"
            @click="toggleFilterFromEvent"
          >
            {{ filter.label }}
          </button>
        </div>
      </div>

      <!-- Cuisines -->
      <UCollapsible
        class="border border-border-level-200 dark:border-border-level-700 mb-4 rounded-lg"
        default-open
      >
        <template #default="{ open }">
          <button
            type="button"
            class="dark:hover:bg-surface-level-700 flex hover:bg-surface-level-50 items-center justify-between p-4 text-left transition-colors w-full"
          >
            <span class="dark:text-content-inverse font-medium text-content-level-900 text-sm">{{
              $t('commerce.filterModal.cuisines')
            }}</span>

            <ChevronUp
              class="h-4 text-content-level-400 transition-transform w-4"
              :class="{ 'rotate-180': !open }"
            />
          </button>
        </template>

        <template #content>
          <div class="pb-4 px-4">
            <div class="gap-3 grid grid-cols-3">
              <div
                v-for="cuisine in visibleCuisines"
                :key="cuisine.id"
                class="cursor-pointer flex flex-col items-center p-3 rounded-lg space-y-2 transition-colors"
                :class="[
                  cuisine.isSelected
                    ? 'bg-primary/10 dark:bg-primary/20 border border-primary'
                    : 'hover:bg-surface-level-50 dark:hover:bg-surface-level-700'
                ]"
                :data-cuisine-id="cuisine.id"
                @click="toggleCuisineFromEvent"
              >
                <div
                  class="bg-surface-level-100 dark:bg-surface-level-700 flex h-8 items-center justify-center rounded-full w-8"
                >
                  <component
                    :is="cuisine.icon"
                    class="h-4 w-4"
                    :class="
                      cuisine.isSelected
                        ? 'text-primary'
                        : 'text-content-level-600 dark:text-content-level-300'
                    "
                  />
                </div>

                <span
                  class="dark:text-content-level-300 text-center text-content-level-700 text-xs"
                  >{{ cuisine.name }}</span
                >
              </div>

              <!-- More button -->
              <div
                v-if="hiddenCuisines.length > 0"
                class="border-border-level-300 cursor-pointer dark:border-border-level-600 dark:hover:border-border-level-500 flex flex-col hover:border-border-level-400 items-center p-3 rounded-lg space-y-2 transition-colors"
                @click="toggleMoreCuisines"
              >
                <div
                  class="bg-surface-level-100 dark:bg-surface-level-700 flex h-8 items-center justify-center rounded-full w-8"
                >
                  <EllipsisHorizontal
                    class="dark:text-content-level-300 h-4 text-content-level-600 w-4"
                  />
                </div>

                <span
                  class="dark:text-content-level-300 text-center text-content-level-700 text-xs"
                >
                  {{ $t('commerce.filterModal.more') }}
                </span>
              </div>
            </div>

            <!-- Hidden cuisines -->
            <div
              v-if="showMoreCuisines && hiddenCuisines.length > 0"
              class="border-border-level-200 border-t dark:border-border-level-700 mt-4 pt-4"
            >
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="cuisine in hiddenCuisines"
                  :key="cuisine.id"
                  type="button"
                  class="border font-medium inline-flex items-center px-3 py-1.5 rounded-full text-xs transition-colors"
                  :class="[
                    cuisine.isSelected
                      ? 'border-primary text-primary bg-primary/10 dark:bg-primary/20'
                      : 'border-border-level-300 text-content-level-600 dark:border-border-level-600 dark:text-content-level-400 bg-surface-level-100 dark:bg-surface-level-800 hover:bg-primary/10 dark:hover:bg-primary/20 hover:border-primary hover:text-primary'
                  ]"
                  :data-cuisine-id="cuisine.id"
                  @click="toggleCuisineFromEvent"
                >
                  <component
                    :is="cuisine.icon"
                    class="h-3 mr-1.5 w-3"
                    :class="
                      cuisine.isSelected
                        ? 'text-primary'
                        : 'text-content-level-600 dark:text-content-level-300'
                    "
                  />
                  {{ cuisine.name }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </UCollapsible>

      <!-- Dietary options -->
      <UCollapsible
        class="border border-border-level-200 dark:border-border-level-700 mb-4 rounded-lg"
        default-open
      >
        <template #default="{ open }">
          <button
            type="button"
            class="dark:hover:bg-surface-level-700 flex hover:bg-surface-level-50 items-center justify-between p-4 text-left transition-colors w-full"
          >
            <span class="dark:text-content-inverse font-medium text-content-level-900 text-sm">{{
              $t('commerce.filterModal.dietaryOptions')
            }}</span>

            <ChevronUp
              class="h-4 text-content-level-400 transition-transform w-4"
              :class="{ 'rotate-180': !open }"
            />
          </button>
        </template>

        <template #content>
          <div class="pb-4 px-4">
            <div class="flex flex-wrap gap-2">
              <button
                v-for="option in dietaryOptions"
                :key="option.id"
                type="button"
                class="font-medium inline-flex items-center px-3 py-1.5 rounded-full space-x-2 text-xs transition-colors"
                :class="[
                  option.isActive
                    ? 'bg-primary text-content-inverse'
                    : 'bg-surface-level-100 dark:bg-surface-level-700 text-content-level-700 dark:text-content-level-300 hover:bg-surface-level-200 dark:hover:bg-surface-level-600'
                ]"
                :data-dietary-id="option.id"
                @click="toggleDietaryFromEvent"
              >
                <UiDietaryIcon
                  v-if="option.icon"
                  :name="option.icon"
                  class="h-4 w-4"
                />

                <span>{{ option.label }}</span>
              </button>
            </div>
          </div>
        </template>
      </UCollapsible>
    </div>

    <template #footer>
      <button
        type="button"
        class="border border-border-level-300 dark:border-border-level-600 dark:hover:bg-surface-level-700 dark:text-content-level-300 flex-1 hover:bg-surface-level-50 px-4 py-2 rounded-lg text-content-level-700 transition-colors"
        @click="clearAllFilters"
      >
        {{ $t('commerce.filterModal.clear') }}
      </button>

      <button
        type="button"
        class="bg-primary flex-1 hover:bg-primary/90 px-4 py-2 rounded-lg text-content-inverse transition-colors"
        @click="applyFilters"
      >
        {{ $t('commerce.filterModal.apply') }}
      </button>
    </template>
  </FullScreenPanel>
</template>

<script setup lang="ts">
import FullScreenPanel from '~/components/shared/dialogs/FullScreenPanel.vue'
import { useFiltersStore } from '~/stores/useFiltersStore'

import {
  ChevronUpIcon as ChevronUp,
  EllipsisHorizontalIcon as EllipsisHorizontal
} from '@heroicons/vue/24/outline'
import { UCollapsible } from '#components'
import type { CuisineIconComponent } from '~/utils/CuisineIconComponent'
import { resolveCuisineIconComponent } from '~/utils/resolveCuisineIconComponent'
import { resolveCuisineIconName } from '~/utils/resolveCuisineIconName'
import UiDietaryIcon from '~/components/DietaryIcon.vue'

interface MealzFilter {
  id: string
  label: string
  isActive: boolean
}
interface CuisineOption {
  id: string | number
  name: string
  slug?: string
  icon: CuisineIconComponent
  isSelected: boolean
}
interface DietaryFilterOption {
  id: string | number
  label: string
  slug: string | number
  icon: DietName | null
  isActive: boolean
}
type DietName =
  | 'gluten-free'
  | 'dairy-free'
  | 'nut-free'
  | 'vegetarian'
  | 'vegan'
  | 'low-carb'
  | 'keto'
  | 'halal'
  | 'kosher'

interface SelectedFilters {
  cuisines: (string | number)[]
  dietary: (string | number)[]
  otherFilters: string[]
}

interface FilterModalProps {
  /** Controls whether the modal is visible. */
  isOpen?: boolean
}

const props = defineProps<FilterModalProps>()

const emit = defineEmits<{
  close: []
  applyFilters: [selectedFilters: SelectedFilters]
}>()

// Use the filters store
const filtersStore = useFiltersStore()

// Filter state - connected to filters store
const mealzFilters = ref<MealzFilter[]>([
  { id: 'reliability', isActive: false, label: 'Reliability Rockstar' },
  { id: 'packaging', isActive: false, label: 'Individual packaging' },
  { id: 'new', isActive: false, label: 'New to Mealz' }
])

const cuisines = ref<CuisineOption[]>([])
const dietaryOptions = ref<DietaryFilterOption[]>([])

// More cuisines state
const showMoreCuisines = ref(false)

// Computed properties for cuisines
const visibleCuisines = computed(() => cuisines.value.slice(0, 8))
const hiddenCuisines = computed(() => cuisines.value.slice(8))

// Functions
const closeModal = () => {
  emit('close')
}

const toggleFilter = (filter: MealzFilter) => {
  // Only update local state, don't update store until Apply is clicked
  filter.isActive = !filter.isActive
}

const toggleFilterFromEvent = (event: Event) => {
  const target = event.currentTarget
  if (!(target instanceof HTMLElement)) return
  const filter = mealzFilters.value.find((item) => String(item.id) === target.dataset['filterId'])
  if (filter) toggleFilter(filter)
}

const toggleCuisine = (cuisine: CuisineOption) => {
  // Only update local state, don't update store until Apply is clicked
  if (cuisine.isSelected) {
    cuisine.isSelected = false
  } else {
    // Deselect all other cuisines first
    cuisines.value.forEach((c) => (c.isSelected = false))
    // Then select the clicked cuisine
    cuisine.isSelected = true
  }
}

const toggleCuisineFromEvent = (event: Event) => {
  const target = event.currentTarget
  if (!(target instanceof HTMLElement)) return
  const cuisine = cuisines.value.find((item) => String(item.id) === target.dataset['cuisineId'])
  if (cuisine) toggleCuisine(cuisine)
}

const toggleDietary = (option: DietaryFilterOption) => {
  // Only update local state, don't update store until Apply is clicked
  option.isActive = !option.isActive
}

const toggleDietaryFromEvent = (event: Event) => {
  const target = event.currentTarget
  if (!(target instanceof HTMLElement)) return
  const option = dietaryOptions.value.find(
    (item) => String(item.id) === target.dataset['dietaryId']
  )
  if (option) toggleDietary(option)
}

const toggleMoreCuisines = () => {
  showMoreCuisines.value = !showMoreCuisines.value
}

const clearAllFilters = () => {
  // Clear local filter states
  mealzFilters.value.forEach((filter) => (filter.isActive = false))

  // Clear all filters store states
  filtersStore.clearSelectedCategory()
  filtersStore.clearDietaryOptions()
  filtersStore.clearRatings()
  filtersStore.clearOtherFilters()

  // Reload categories and dietary options to reflect cleared state
  loadCategories()
  loadDietaryOptions()
}

// Use the app data composable
const { categories: appCategories, dietaryOptions: appDietaryOptions, fetchAppData } = useAppData()

// Load categories from app data
const loadCategories = () => {
  cuisines.value = appCategories.value.map((category) => {
    const slug = typeof category.slug === 'string' ? category.slug : String(category.id)
    return {
      icon: resolveCuisineIconComponent(resolveCuisineIconName(slug)),
      id: category.id,
      isSelected:
        filtersStore.selectedCategory?.slug === category.slug ||
        filtersStore.selectedCategory?.id === category.id,
      name: category.name ?? '',
      slug
    }
  })
}

// Helper to map slug to icon name (same as store detail page)
const getDietaryIconName = (slug: string | number): DietName | null => {
  const validSlugs: DietName[] = [
    'gluten-free',
    'dairy-free',
    'nut-free',
    'vegetarian',
    'vegan',
    'low-carb',
    'keto',
    'halal',
    'kosher'
  ]
  const normalizedSlug = String(slug)
  const isDietName = (value: string): value is DietName => validSlugs.some((slug) => slug === value)
  return isDietName(normalizedSlug) ? normalizedSlug : null
}

// Load dietary options from app data
const loadDietaryOptions = () => {
  dietaryOptions.value = appDietaryOptions.value.map((option): DietaryFilterOption => {
    const slug = option.slug ?? option.id
    return {
      icon: getDietaryIconName(slug),
      id: option.id,
      isActive: filtersStore.selectedDietaryOptions.includes(option.id),
      label: option.label,
      slug: slug
    }
  })
}

// Load filters from store
const loadMealzFilters = () => {
  mealzFilters.value.forEach((filter) => {
    filter.isActive = filtersStore.selectedOtherFilters.includes(filter.id)
  })
}

const applyFilters = () => {
  // Update the filters store with the selected options
  const selectedCategory = cuisines.value.find((c) => c.isSelected)
  if (selectedCategory) {
    filtersStore.setSelectedCategory(selectedCategory)
  } else {
    filtersStore.clearSelectedCategory()
  }

  // Update dietary options
  const selectedDietaryIds = dietaryOptions.value.filter((d) => d.isActive).map((d) => d.id)
  filtersStore.selectedDietaryOptions = selectedDietaryIds

  // Update other filters
  const selectedOtherIds = mealzFilters.value.filter((f) => f.isActive).map((f) => f.id)
  filtersStore.selectedOtherFilters = selectedOtherIds

  // Emit the selected filters
  const categoryValue = selectedCategory?.slug ?? selectedCategory?.id
  const selectedFilters: SelectedFilters = {
    cuisines: categoryValue == null ? [] : [categoryValue],
    dietary: selectedDietaryIds,
    otherFilters: selectedOtherIds
  }

  emit('applyFilters', selectedFilters)

  // Watchers will trigger search automatically
  closeModal()
}

// Initialize on mount
onMounted(async () => {
  // Fetch app data first, then load categories and dietary options
  await fetchAppData()
  loadCategories()
  loadDietaryOptions()
  loadMealzFilters()
})

// Re-sync local state from the filters store every time the modal opens
watch(
  () => props.isOpen,
  (isOpen) => {
    if (!isOpen) return
    loadCategories()
    loadDietaryOptions()
    loadMealzFilters()
  }
)
</script>
